import { useCallback, useEffect, useState } from "react";
import { knowledgeBaseAPI } from "../services/knowledgeBaseAPI";

export const useKnowledgeBaseIntake = (projectId) => {
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [rowsRes, optionsRes] = await Promise.all([
        knowledgeBaseAPI.list(projectId),
        knowledgeBaseAPI.options(projectId),
      ]);
      setRows(rowsRes.data || []);
      setOptions(optionsRes.data || {});
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const indexingRow = rows.find(
      (row) => row.kb_status === "indexing" && row.kb_ingestion_job_id,
    );
    const extractingRow = rows.find(
      (row) => ["queued", "extracting"].includes(row.extraction_status),
    );
    if ((!indexingRow && !extractingRow) || !projectId) return undefined;

    const timer = window.setTimeout(async () => {
      try {
        // Refresh Bedrock status while indexing. Extraction status is persisted
        // separately and is picked up by the normal list reload.
        if (indexingRow) {
          await knowledgeBaseAPI.refresh(projectId, indexingRow.id);
        }
        await load();
      } catch (e) {
        setError(e);
      }
    }, 10000);

    return () => window.clearTimeout(timer);
  }, [rows, projectId, load]);

  const run = async (action) => {
    setError(null);
    try {
      const result = await action();
      await load();
      return result?.data;
    } catch (e) {
      setError(e);
      throw e;
    }
  };

  return { rows, options, loading, error, reload: load, run };
};
